// Drives `partials/translate-widget.hbs`: a switch that auto-translates the page with Google Translate into the app's or browser's language.

import {byId, getOsFromUserAgent, matchSwitchToOs, scrollToHash} from './lib/dom.ts'
import {getParameter, LANGUAGE_KEY} from './lib/params.ts'

const AUTO_TRANSLATE_i18n: Record<string, string> = {
    en: "Auto-Translate",
    'zh-CN': "自动翻译",
    'zh-TW': "自動翻譯",
    hi: "स्वत: अनुवाद",
    es: "Traducción automática",
    ar: "ترجمة تلقائية",
    bn: "স্বয়ংক্রিয় অনুবাদ",
    pt: "Tradução automática",
    id: "Terjemahan Otomatis",
    ru: "Автоматический перевод",
    ur: "خود کار ترجمہ",
    ja: "自動翻訳",
    fr: "Auto-traduction",
    de: "Automatische Übersetzung",
    te: "స్వయంచాలక అనువాదం",
    tr: "Otomatik Çeviri",
    ta: "தானியங்கி மொழிபெயர்ப்பு",
    vi: "Tự động dịch",
    ko: "자동 번역",
    mr: "स्वयंचलित भाषांतर",
    it: "Traduzione automatica",
    th: "แปลอัตโนมัติ",
    fa: "ترجمه خودکار",
    pl: "Automatyczne tłumaczenie",
    uk: "Автоматичний переклад",
    ml: "സ്വയമേവ വിവർത്തനം",
    gu: "સ્વયં અનુવાદ",
    nl: "Automatisch vertalen",
    ro: "Traducere automată",
    my: "အလိုအလျောက်ဘာသာပြန်ခြင်း",
    el: "Αυτόματη μετάφραση",
    hu: "Automatikus fordítás",
    cs: "Automatický překlad",
    sw: "Tafsiri Otomatiki",
    si: "ස්වයංක්‍රීය පරිවර්තනය",
    he: "תרגום אוטומטי",
    bg: "Автоматичен превод",
    km: "ការបកប្រែដោយស្វ័យប្រវត្តិ",
    sv: "Automatisk översättning",
    fi: "Automaattinen käännös",
    da: "Automatisk oversættelse",
    no: "Automatisk oversettelse"
}

let translationActive = false
const translationObserver = new MutationObserver(translationHasChanged)
const translateElementObserver = new MutationObserver(translateElementAvailable)
const spinner = document.querySelector('#translate-widget > div.spinner')
const checkbox = document.querySelector<HTMLInputElement>('#translate-widget > label > input[type="checkbox"]')

window.translateOn = translateOn
window.googleTranslateElementInit = googleTranslateElementInit

initAutoTranslateIfRequired()

function getElementByXpath(doc: Document, path: string) {
    return doc.evaluate(path, doc, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue as HTMLElement | null
}


// TRANSLATION HANDLING

function translateOn() {
    console.log("Translate on")
    spinner?.classList.remove('isHidden')
    const menuFrame = getMenuFrame()
    if (!menuFrame) return

    // HACK: no idea why we need to turn off and on, but it works
    clickItem(menuFrame, false)
    clickItem(menuFrame, true)
    clickItem(menuFrame, true) // second click needed to change from "select language" to actual language
    translationActive = true
}

function translateOff() {
    console.log("Translate off")
    const menuFrame = getMenuFrame()
    if (!menuFrame) return

    // HACK: no idea why we need to turn off and on, but it (mostly) works (except: https://trello.com/c/YbGXocmJ)
    clickItem(menuFrame, false)
    clickItem(menuFrame, true)
    translationActive = false
}

/** Clicks the item of Google's language menu that turns the translation on or off */
function clickItem(menuFrame: HTMLIFrameElement, on: boolean) {
    const menuDocument = menuFrame.contentWindow?.document
    if (!menuDocument) return
    const index = on ? 2 : 1
    getElementByXpath(menuDocument, `//*[@id=":1.menuBody"]/table/tbody/tr/td/a[${index}]/div/span[2]`)?.click()
}

function getMenuFrame() {
    // WARNING: grabbing the menuFrame is super highly coupled!
    // We can't rely on this class name for long!

    // How to get the menu frame:
    // 1. Make sure that ".goog-te-gadget" and iframes are visible (comment "display: none;" in auto-translate.scss)
    // 2. Click on the now visible element behind the "Auto-Translate" text
    // 3. The iframe that contains the list with languages is the "menu frame"
    return document.querySelector<HTMLIFrameElement>('.VIpgJd-ZVi9od-xl07Ob-OEVmcd')
}

// OBSERVER CALLBACKS

function translateElementAvailable() {
    console.log("Translate element available!")

    translateElementObserver.disconnect()
    byId('translate-widget').classList.add('isVisible')
    setTimeout(scrollToHash, 350) // the widget pushed the content down: 0.3s animation time for isVisible + 50ms slack

    // inject CSS into iframe
    const menuDocument = document.querySelector<HTMLIFrameElement>('.goog-te-menu-frame')?.contentWindow?.document
    if (menuDocument) {
        const cssLink = document.createElement('link')
        cssLink.href = '/assets/css/auto-translate.css'
        cssLink.rel = 'stylesheet'
        menuDocument.head.appendChild(cssLink)
    }

    checkbox?.addEventListener('change', () => {
        if (translationActive) translateOff()
        else translateOn()
    })
}

function translationHasChanged() {
    console.log("Translation has changed!")
    const firstHeading = document.getElementsByTagName('h1')[0]?.innerHTML ?? ''
    // Google inserts translations within <font> tags. We don't use them. Hence, the heading must be translated if there's one.
    if (firstHeading.includes('<font')) translationActive = true
    if (checkbox) checkbox.checked = translationActive
    spinner?.classList.add('isHidden')
}

// TRANSLATE INIT

function initTranslateWidget(langToTranslateTo: string) {
    const widgetText = document.querySelector('#translate-widget > p')
    if (widgetText) widgetText.textContent = AUTO_TRANSLATE_i18n[langToTranslateTo] || AUTO_TRANSLATE_i18n.en
    const languageSwitch = document.querySelector('#translate-widget > .switch')
    if (languageSwitch) matchSwitchToOs(languageSwitch)
    if (getOsFromUserAgent() === 'android') spinner?.classList.add('android-spinner')
}

/** Called by the Google script once loaded */
function googleTranslateElementInit() {
    const langToTranslateTo = getLanguageForTranslateWidget()

    initTranslateWidget(langToTranslateTo)
    const title = document.querySelector('h1')
    if (title) translationObserver.observe(title, {childList: true})
    translateElementObserver.observe(byId('google_translate_element'), {childList: true})
    const documentLang = document.documentElement.lang
    new google.translate.TranslateElement({
        pageLanguage: documentLang, includedLanguages: `${documentLang},${langToTranslateTo}`,
        layout: google.translate.TranslateElement.InlineLayout.SIMPLE
    }, 'google_translate_element')
}

function initAutoTranslateIfRequired() {
    // Don't show auto-translate if page is already in this language
    if (getLanguageForTranslateWidget() === document.documentElement.lang) return

    // Dynamically add Google Translate dependency which calls "googleTranslateElementInit()" when loaded
    const script = document.createElement('script')
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'
    document.head.appendChild(script)

    translationHasChanged()
}

function getLanguageForTranslateWidget() {
    const appLanguage = getParameter(LANGUAGE_KEY)
    const browserLanguage = navigator.language
    console.log("App lang", appLanguage, "browserLang", browserLanguage)
    // Get language from params (cause user came from app) or read browser language
    const lang = appLanguage || browserLanguage

    // HACK Migration for Android <= 1.0.0 TODO remove this if when no one runs Android <= 1.0.0
    if (lang === 'zh') return 'zh-CN' // Android used to send just "zh". We cannot separate Traditional from Simplified Chinese

    // Exceptions for Chinese
    if (lang.startsWith('zh-Hans')) return 'zh-CN'
    if (lang.startsWith('zh-Hant')) return 'zh-TW'

    return lang.split('-')[0]
}
